import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Button, Card, Empty, Popconfirm, Space, Tag, Tooltip, Typography } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import SearchBarComp from "../../../commons/SearchBar/SearchBar";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { deleteMeasurement, getMeta, listMeasurements } from "./api";
import { PendingTag } from "../../../offline/OfflineNotice";
import { formatM, formatM2 } from "./format";
import "./measurements.css";

const Measurements = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const history = useHistory();
  const isMobile = useIsMobile();
  const [meta, setMeta] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listMeasurements({ searchText });
      setData(res.data.data);
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getMeta()
      .then(res => setMeta(res.data))
      .catch(generalErrorAlert);
  }, []);

  useEffect(() => {
    load();
  }, [searchText]);

  const handleDelete = async id => {
    try {
      await deleteMeasurement(id);
      Success(t("Measurement deleted"));
      load();
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const open = id => history.push(`/user/measurements/${id}`);

  const columns = [
    {
      title: t("Measurement"),
      key: "title",
      mobile: "title",
      render: (_, item) => (
        <div>
          <Link to={`/user/measurements/${item._id}`}>
            <Typography.Text strong>{item.title}</Typography.Text>
          </Link>
          <div className="ms-small">
            {[item.date && dayjs(item.date).format("DD.MM.YYYY"), item.address]
              .filter(Boolean)
              .join(" · ")}
          </div>
          <Space size={[4, 4]} wrap>
            {item.project?.projectName && <Tag color="blue">{item.project.projectName}</Tag>}
            {item.warningCount > 0 && <Tag color="warning">{t("Please check")}</Tag>}
            <PendingTag module="measurements" record={item} />
          </Space>
        </div>
      )
    },
    { title: t("Rooms"), dataIndex: "roomCount", key: "rooms", width: 80, align: "right" },
    {
      title: t("Floor"),
      key: "floor",
      width: 120,
      align: "right",
      render: (_, item) => formatM2(item.totals?.floorNet, lng)
    },
    {
      title: t("Walls (net)"),
      key: "walls",
      width: 130,
      align: "right",
      render: (_, item) => formatM2(item.totals?.wallNet, lng)
    },
    {
      title: t("Skirting"),
      key: "skirting",
      width: 110,
      align: "right",
      render: (_, item) => formatM(item.totals?.skirting, lng)
    },
    {
      title: "",
      key: "actions",
      mobile: "actions",
      width: 96,
      align: "right",
      render: (_, item) => (
        <Space size={4}>
          <Tooltip title={t("Edit")}>
            <Button icon={<EditOutlined />} aria-label={t("Edit")} onClick={() => open(item._id)} />
          </Tooltip>
          {meta?.can.delete && (
            <Popconfirm
              title={t("Delete this measurement?")}
              okText={t("Delete")}
              okButtonProps={{ danger: true }}
              cancelText={t("Cancel")}
              onConfirm={() => handleDelete(item._id)}
            >
              <Button danger icon={<DeleteOutlined />} aria-label={t("Delete")} />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  const table = (
    <ResponsiveTable
      rowKey="_id"
      loading={loading}
      columns={columns}
      dataSource={data}
      mobileAvatar={false}
      pagination={data.length > 50 ? { pageSize: 50 } : false}
      scroll={{ x: 720 }}
    />
  );

  const isEmpty = !loading && !data.length && !searchText;

  return (
    <div className="measurements-page">
      <PageToolbar
        filters={<SearchBarComp handleSearch={setSearchText} />}
        actions={
          meta?.can.create && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => open("new")}>
              {t("New measurement")}
            </Button>
          )
        }
      />
      {isEmpty ? (
        <Card>
          <Empty
            description={
              <>
                <Typography.Title level={5}>{t("Measure rooms on site")}</Typography.Title>
                <Typography.Paragraph type="secondary" className="ms-empty-text">
                  {t(
                    "Enter dimensions by hand, dictate them, or measure them with the camera. Floor, wall and ceiling areas are calculated with deductions as in the VOB."
                  )}
                </Typography.Paragraph>
              </>
            }
          >
            {meta?.can.create && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => open("new")}>
                {t("New measurement")}
              </Button>
            )}
          </Empty>
        </Card>
      ) : isMobile ? (
        table
      ) : (
        <Card styles={{ body: { padding: 0 } }}>{table}</Card>
      )}
    </div>
  );
};

export default Measurements;
